import {
  Box,
  Button,
  Flex,
  Heading1,
  Icon,
  IconLockSmallFilled,
  Input,
  Link,
  TextSmall,
  UITextBody,
} from '@teamleader/ahoy';
import { translate, Translation } from '@teamleader/i18n';
import React, { useCallback, useState } from 'react';
import { useMultiFactorAuthenticationContext } from './MultiFactorAuthenticationContext';
import useMultiFactorAuthenticationActivate from './useMultifactorAuthenticationActivate';
import redirectToApplication from '../../../Infrastructure/Routing/redirectToApplication';
import UrlGenerator from '../../../Infrastructure/Routing/UrlGenerator';
import { GoogleAuthenticatorIcon, MicrosoftAuthenticatorIcon, AuthyIcon, OnePasswordIcon } from './icons';
import BackToLoginLink from './BackToLoginLink';
import styles from './styles.css';
import useMultifactorAuthenticationGenerate from './useMultiFactorAuthenticationGenerate';
import useMultiFactorAuthenticationSkip from './useMultiFactorAuthenticationSkip';
import useEffectOnInitialize from './useEffectOnInitialize';
import { EnforcementStatus } from '../../../Infrastructure/API/MultiFactorAuthentication/list';

interface TotpActivationProps {
  onGoBackToLoginClick: () => void;
}

const TotpActivation = ({ onGoBackToLoginClick }: TotpActivationProps) => {
  const { authToken, enforcementStatus } = useMultiFactorAuthenticationContext();
  const {
    response: generatedResponse,
    generate,
    error: generationError,
    loading: generating,
  } = useMultifactorAuthenticationGenerate({ authToken });
  const {
    activate,
    fetched: activated,
    error: activationError,
    loading: activating,
  } = useMultiFactorAuthenticationActivate({ authToken });
  const { skip, fetched: skipped } = useMultiFactorAuthenticationSkip({ authToken });

  const { qr_code: qrCode, secret } = generatedResponse?.data ?? {};

  const [token, setToken] = useState('');
  const [tokenError, setTokenError] = useState<string | boolean>();

  useEffectOnInitialize(() => {
    generate();
  });

  const handleActivate = useCallback(() => {
    const trimmedCode = token.trim();

    if (trimmedCode.length === 0) {
      setTokenError(true);
      return;
    }

    setTokenError(undefined);

    activate(trimmedCode);
  }, [activate, token]);

  const handleSkip = useCallback(() => {
    skip();
  }, [skip]);

  if (activated || skipped) {
    const appUrl = UrlGenerator().appRoot();
    redirectToApplication(appUrl);
  }

  return (
    <Flex direction="column" alignItems="stretch" gap={4}>
      <Flex direction="column" alignItems="flex-start">
        <BackToLoginLink className={styles.back} onClick={onGoBackToLoginClick} />

        <Flex direction="column" alignItems="flex-start" marginTop={3} marginBottom={4}>
          <Heading1>{translate('mfa.totpActivation.title')}</Heading1>
          <TextSmall color="neutral" tint="darkest">
            {translate('mfa.totpActivation.description')}
          </TextSmall>
        </Flex>
      </Flex>

      <Flex gap={3}>
        <Box className={styles['step-number']}>1</Box>
        <Flex
          direction="column"
          flex={1}
          borderBottomWidth={1}
          borderColor="neutral"
          borderTint="normal"
          paddingBottom={4}
        >
          <UITextBody>{translate('mfa.totpActivation.step1.title')}</UITextBody>
          <TextSmall color="neutral" tint="darkest">
            {translate('mfa.totpActivation.step1.description')}
          </TextSmall>
          <Flex gap={2} marginTop={3} marginBottom={2} alignItems="center">
            <GoogleAuthenticatorIcon />
            <MicrosoftAuthenticatorIcon />
            <AuthyIcon />
            <OnePasswordIcon />
          </Flex>
        </Flex>
      </Flex>

      <Flex gap={3}>
        <Box className={styles['step-number']}>2</Box>
        <Flex
          direction="column"
          flex={1}
          alignItems="flex-start"
          borderBottomWidth={1}
          borderColor="neutral"
          borderTint="normal"
          paddingBottom={4}
        >
          <UITextBody>{translate('mfa.totpActivation.step2.title')}</UITextBody>

          {qrCode ? (
            <>
              <TextSmall color="neutral" tint="darkest">
                {translate('mfa.totpActivation.step2.description', { secret: secret ?? '' })}
              </TextSmall>

              <Flex
                borderWidth={2}
                marginTop={3}
                borderColor="neutral"
                borderTint="normal"
                borderRadius="rounded"
                className={styles['qr-code']}
                dangerouslySetInnerHTML={{ __html: qrCode.content }}
              />
            </>
          ) : (
            <>
              {generating && (
                <TextSmall color="neutral" tint="darkest">
                  {translate('mfa.totpActivation.loading')}
                </TextSmall>
              )}
              {generationError && !generating && (
                <TextSmall color="ruby" tint="dark">
                  <Translation
                    id="mfa.totpActivation.generateError"
                    values={{
                      link: (chunks: string[]): JSX.Element => (
                        <Link
                          inherit={false}
                          element="a"
                          onClick={() => {
                            generate();
                          }}
                        >
                          {chunks}
                        </Link>
                      ),
                    }}
                  />
                </TextSmall>
              )}
            </>
          )}
        </Flex>
      </Flex>

      <Flex gap={3}>
        <Box className={styles['step-number']}>3</Box>
        <Flex direction="column" flex={1} paddingBottom={4}>
          <UITextBody>{translate('mfa.totpActivation.step3.title')}</UITextBody>
          <TextSmall color="neutral" tint="darkest">
            {translate('mfa.totpActivation.step3.description')}
          </TextSmall>
          <Input
            marginTop={3}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={translate('mfaCode')}
            prefix={
              <Icon color="neutral" tint="darkest">
                <IconLockSmallFilled />
              </Icon>
            }
            value={token}
            onChange={(_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
              setToken(value);
              setTokenError(undefined);
            }}
            error={tokenError}
            size="large"
          />
          {activationError && (
            <TextSmall color="ruby" tint="dark">
              {translate('mfa.totpActivation.activateError')}
            </TextSmall>
          )}
        </Flex>
      </Flex>

      <Flex direction="column" alignItems="stretch" gap={3}>
        <Button size="large" level="primary" fullWidth type="button" processing={activating} onClick={handleActivate}>
          {translate('mfa.totpActivation.finishSetup')}
        </Button>
        {enforcementStatus === EnforcementStatus.PendingEnforcement && (
          <Flex justifyContent="center">
            <Button
              level="link"
              onClick={handleSkip}
            >
              <TextSmall color="aqua" tint="dark">
                {translate('mfa.totpActivation.skipForLater')}
              </TextSmall>
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default TotpActivation;
